import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

export default function WhatsAppContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [optInFilter, setOptInFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showList, setShowList] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', tags: '' });
  const [csvText, setCsvText] = useState('');
  const [listForm, setListForm] = useState({ name: '', description: '' });
  const [selected, setSelected] = useState([]);

  const token = localStorage.getItem('dentalToken');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (optInFilter) params.optInStatus = optInFilter;
      const { data } = await axios.get(`${API}/api/saas/wa-contacts`, { headers, params });
      setContacts(data.contacts || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, optInFilter]);

  const addContact = async () => {
    if (!form.name || !form.phone) return alert('Name and phone required');
    try {
      await axios.post(`${API}/api/saas/wa-contacts`, {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
      }, { headers });
      setForm({ name: '', phone: '', email: '', tags: '' });
      setShowAdd(false);
      load();
    } catch (err) {
      alert('Failed to add contact');
    }
  };

  const importCsv = async () => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return alert('CSV needs header + rows');
    const headerRow = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = lines.slice(1).map(line => {
      const cells = line.split(',').map(c => c.trim());
      const obj = {};
      headerRow.forEach((h, i) => { obj[h] = cells[i]; });
      return obj;
    }).filter(r => r.name && r.phone);

    try {
      const { data } = await axios.post(`${API}/api/saas/wa-contacts/import`,
        { contacts: rows }, { headers });
      alert(`Imported ${data.imported}/${data.total}`);
      setCsvText('');
      setShowImport(false);
      load();
    } catch (err) {
      alert('Import failed');
    }
  };

  const createList = async () => {
    if (!listForm.name || selected.length === 0) return alert('Name + select contacts');
    try {
      await axios.post(`${API}/api/saas/wa-contacts/lists`, {
        ...listForm,
        isDynamic: false,
        staticContactIds: selected
      }, { headers });
      setListForm({ name: '', description: '' });
      setSelected([]);
      setShowList(false);
      alert('Audience created');
    } catch (err) {
      alert('Failed to create list');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const statusBadge = (s) => {
    const colors = { 'opted-in': '#10b981', 'pending': '#f59e0b', 'opted-out': '#ef4444' };
    return <span style={{ background: colors[s] || '#888', color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>{s}</span>;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📇 WhatsApp Contacts</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowAdd(true)} style={btnPrimary}>+ Add Contact</button>
          <button onClick={() => setShowImport(true)} style={btnSecondary}>📥 Import CSV</button>
          {selected.length > 0 && (
            <button onClick={() => setShowList(true)} style={btnSuccess}>
              Create Audience ({selected.length})
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          placeholder="Search name or phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
          style={{ ...input, flex: 1 }}
        />
        <select value={optInFilter} onChange={e => { setOptInFilter(e.target.value); setPage(1); }} style={input}>
          <option value="">All statuses</option>
          <option value="opted-in">Opted In</option>
          <option value="pending">Pending</option>
          <option value="opted-out">Opted Out</option>
        </select>
        <button onClick={() => { setPage(1); load(); }} style={btnSecondary}>Search</button>
      </div>

      {loading ? <div>Loading...</div> : (
        <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={th}></th>
                <th style={th}>Name</th>
                <th style={th}>Phone</th>
                <th style={th}>Email</th>
                <th style={th}>Tags</th>
                <th style={th}>Opt-in</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={td}>
                    <input type="checkbox" checked={selected.includes(c._id)} onChange={() => toggleSelect(c._id)} />
                  </td>
                  <td style={td}>{c.name}</td>
                  <td style={td}>{c.phone}</td>
                  <td style={td}>{c.email || '-'}</td>
                  <td style={td}>{(c.tags || []).join(', ') || '-'}</td>
                  <td style={td}>{statusBadge(c.optInStatus)}</td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#888', padding: 40 }}>
                  No contacts yet. Add one or import CSV.
                </td></tr>
              )}
            </tbody>
          </table>
          <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', background: '#f9fafb' }}>
            <span>Total: {total}</span>
            <div>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={btnSecondary}>Prev</button>
              <span style={{ margin: '0 12px' }}>Page {page}</span>
              <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} style={btnSecondary}>Next</button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="Add Contact">
          <input placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={input} />
          <input placeholder="Phone * (e.g. +919876543210)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={input} />
          <input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={input} />
          <input placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} style={input} />
          <button onClick={addContact} style={btnPrimary}>Save</button>
        </Modal>
      )}

      {showImport && (
        <Modal onClose={() => setShowImport(false)} title="Import CSV">
          <p style={{ fontSize: 13, color: '#666' }}>
            Format: <code>name,phone,email,tags</code> (first row = headers)
          </p>
          <textarea
            placeholder="name,phone,email,tags&#10;Rajesh,+919876543210,r@x.com,vip"
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
            style={{ ...input, minHeight: 180, fontFamily: 'monospace' }}
          />
          <button onClick={importCsv} style={btnPrimary}>Import</button>
        </Modal>
      )}

      {showList && (
        <Modal onClose={() => setShowList(false)} title={`Create Audience (${selected.length} contacts)`}>
          <input placeholder="Audience name *" value={listForm.name} onChange={e => setListForm({ ...listForm, name: e.target.value })} style={input} />
          <input placeholder="Description" value={listForm.description} onChange={e => setListForm({ ...listForm, description: e.target.value })} style={input} />
          <button onClick={createList} style={btnSuccess}>Create Audience</button>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const input = { padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 10, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' };
const btnSuccess = { padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const th = { padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#374151' };
const td = { padding: 12, fontSize: 14 };
